import React from 'react';
import { BarChart2Icon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, MinusSquareIcon, DollarSignIcon, FileTextIcon, PlusIcon, ClockIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function CreditNoteDashboard({
  creditNotes = [],
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalCreditNotes = creditNotes.length;
  const totalAmount = creditNotes.reduce((sum, cn) => sum + cn.amount, 0);
  const appliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.appliedAmount || 0), 0);
  const unappliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.unappliedAmount || 0), 0);
  // Count by status
  const statusCounts = creditNotes.reduce((counts, cn) => {
    counts[cn.status] = (counts[cn.status] || 0) + 1;
    return counts;
  }, {});
  // Recent credit notes (last 5)
  const recentCreditNotes = [...creditNotes].sort((a, b) => new Date(b.creditNoteDate) - new Date(a.creditNoteDate)).slice(0, 5);
  return <div data-id="element-3071">
      <div className="mb-8 flex justify-between items-center" data-id="element-3072">
        <div data-id="element-3073">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3074">
            Credit Notes Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3075">
            Overview of credit note status and activity
          </p>
        </div>
        <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3076">
          <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-3077" />
          New Credit Note
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-3078">
        {/* Total Credit Notes */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3079">
          <div className="px-4 py-5 sm:p-6" data-id="element-3080">
            <div className="flex items-center" data-id="element-3081">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-3082">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-3083" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3084">
                <dl data-id="element-3085">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3086">
                    Total Credit Notes
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3087">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3088">
                      {totalCreditNotes}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3089">
            <div className="text-sm" data-id="element-3090">
              <a href="#" onClick={() => setCurrentView('credit-note-list')} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3091">
                View all credit notes
              </a>
            </div>
          </div>
        </div>
        {/* Total Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3092">
          <div className="px-4 py-5 sm:p-6" data-id="element-3093">
            <div className="flex items-center" data-id="element-3094">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-3095">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-3096" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3097">
                <dl data-id="element-3098">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3099">
                    Total Credit Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3100">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3101">
                      {formatCurrency(totalAmount, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3102">
            <div className="text-sm" data-id="element-3103">
              <span className="font-medium text-gray-500" data-id="element-3104">
                Applied: {formatCurrency(appliedAmount, 'USD', locale)}
              </span>
            </div>
          </div>
        </div>
        {/* Unapplied Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3105">
          <div className="px-4 py-5 sm:p-6" data-id="element-3106">
            <div className="flex items-center" data-id="element-3107">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-3108">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-3109" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3110">
                <dl data-id="element-3111">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3112">
                    Unapplied Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3113">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3114">
                      {formatCurrency(unappliedAmount, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3115">
            <div className="text-sm" data-id="element-3116">
              <a href="#" className="font-medium text-yellow-600 hover:text-yellow-500" data-id="element-3117">
                {unappliedAmount > 0 ? 'Needs attention' : 'All credits applied'}
              </a>
            </div>
          </div>
        </div>
        {/* Pending Approval */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3118">
          <div className="px-4 py-5 sm:p-6" data-id="element-3119">
            <div className="flex items-center" data-id="element-3120">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-3121">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-3122" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3123">
                <dl data-id="element-3124">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3125">
                    Pending Approval
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3126">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3127">
                      {statusCounts['Pending'] || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3128">
            <div className="text-sm" data-id="element-3129">
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-3130">
                {statusCounts['Pending'] ? 'Review pending credit notes' : 'No pending approvals'}
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-3131">
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3132">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3133">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3134">
              Credit Notes by Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3135">
            <div className="space-y-4" data-id="element-3136">
              <div className="flex items-center justify-between" data-id="element-3137">
                <div className="flex items-center" data-id="element-3138">
                  <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-3139"></div>
                  <span className="text-sm text-gray-500" data-id="element-3140">Draft</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3141">
                  {statusCounts['Draft'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3142">
                <div className="flex items-center" data-id="element-3143">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-3144"></div>
                  <span className="text-sm text-gray-500" data-id="element-3145">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3146">
                  {statusCounts['Pending'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3147">
                <div className="flex items-center" data-id="element-3148">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-3149"></div>
                  <span className="text-sm text-gray-500" data-id="element-3150">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3151">
                  {statusCounts['Approved'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3152">
                <div className="flex items-center" data-id="element-3153">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-3154"></div>
                  <span className="text-sm text-gray-500" data-id="element-3155">Posted</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3156">
                  {statusCounts['Posted'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3157">
                <div className="flex items-center" data-id="element-3158">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-3159"></div>
                  <span className="text-sm text-gray-500" data-id="element-3160">Rejected</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3161">
                  {statusCounts['Rejected'] || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3162">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3163">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3164">
              Application Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3165">
            <div className="flex items-center justify-between mb-2" data-id="element-3166">
              <span className="text-sm font-medium text-gray-500" data-id="element-3167">
                Total Credit Amount
              </span>
              <span className="text-sm font-medium text-gray-900" data-id="element-3168">
                {formatCurrency(totalAmount, 'USD', locale)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-3169">
              <div className="bg-green-600 h-2.5 rounded-full" style={{
              width: `${appliedAmount / totalAmount * 100}%`
            }} data-id="element-3170"></div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3171">
              <div className="bg-green-50 p-4 rounded-md" data-id="element-3172">
                <div className="flex items-center" data-id="element-3173">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3174" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3175">
                    Applied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3176">
                  {formatCurrency(appliedAmount, 'USD', locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3177">
                  {Math.round(appliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-md" data-id="element-3178">
                <div className="flex items-center" data-id="element-3179">
                  <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-2" data-id="element-3180" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3181">
                    Unapplied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3182">
                  {formatCurrency(unappliedAmount, 'USD', locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3183">
                  {Math.round(unappliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Credit Notes */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3184">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3185">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3186">
            Recent Credit Notes
          </h3>
          <a href="#" onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3187">
            View all
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-3188">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-3189">
            <thead className="bg-gray-50" data-id="element-3190">
              <tr data-id="element-3191">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3192">
                  Credit Note #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3193">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3194">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3195">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3196">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-3197">
                  <span className="sr-only" data-id="element-3198">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3199">
              {recentCreditNotes.length > 0 ? recentCreditNotes.map(creditNote => <tr key={creditNote.id} className="hover:bg-gray-50" data-id="element-3200">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-3201">
                      {creditNote.creditNoteNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3202">
                      {creditNote.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3203">
                      {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3204">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3205">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${creditNote.status === 'Approved' ? 'bg-green-100 text-green-800' : creditNote.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : creditNote.status === 'Draft' ? 'bg-gray-100 text-gray-800' : creditNote.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-3206">
                        {creditNote.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3207">
                      <a href="#" onClick={() => {
                  setCurrentView('credit-note-details');
                }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3208">
                        View
                      </a>
                    </td>
                  </tr>) : <tr data-id="element-3209">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3210">
                    No credit notes found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}